import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { getChecklistBySlug } from '../data/checklists';
import NewsletterSignup from '../components/NewsletterSignup';
import { buildTitle, DEFAULT_OG_IMAGE, SITE_URL } from '../utils/seo';

const VERIFY_ENDPOINT = process.env.REACT_APP_VERIFY_ENDPOINT || '/api/verify';

export default function ChecklistDetail() {
  const { slug } = useParams();
  const checklist = getChecklistBySlug(slug);

  const [hasAccess, setHasAccess] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [checked, setChecked] = useState({});
  const storageKey = `checklist_progress_${slug}`;
  const accessKey = `checklistAccess_${slug}`;

  const verifyToken = useCallback(async (token) => {
    setIsVerifying(true);
    try {
      const res = await fetch(`${VERIFY_ENDPOINT}?token=${encodeURIComponent(token)}`);
      const data = await res.json();
      if (data.ok) {
        localStorage.setItem(accessKey, 'granted');
        localStorage.setItem('checklistAccess', 'granted');
        setHasAccess(true);
      }
    } catch (_) {}
    finally { setIsVerifying(false); }
  }, [accessKey]);

  // Load access + progress from localStorage.
  useEffect(() => {
    const access = localStorage.getItem(accessKey) === 'granted'
      || localStorage.getItem('checklistAccess') === 'granted'; // legacy key
    setHasAccess(access);

    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try { setChecked(JSON.parse(saved)); } catch (_) {}
    }

    const token = new URLSearchParams(window.location.search).get('token');
    if (token) verifyToken(token);
  }, [accessKey, storageKey, verifyToken]);

  const grantAccess = (result) => {
    localStorage.setItem(accessKey, 'granted');
    localStorage.setItem('checklistAccess', 'granted');
    setHasAccess(true);
    if (result?.token) verifyToken(result.token);
  };

  const toggleItem = (id) => {
    setChecked(prev => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  const totalItems = checklist?.sections.reduce((sum, s) => sum + s.items.length, 0) || 0;
  const completedItems = Object.values(checked).filter(Boolean).length;
  const progress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  const handlePrint = () => window.print();

  if (!checklist) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-16 text-center">
        <p className="text-[#6a6661]">Checklist not found.</p>
        <Link to="/checklist" className="mt-4 inline-block text-sm uppercase tracking-[0.25em] text-[#2E4036] underline">
          View all checklists
        </Link>
      </main>
    );
  }

  const pageTitle = buildTitle(checklist.title);
  const pageUrl = `${SITE_URL}/checklist/${slug}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={checklist.description} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={checklist.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={checklist.description} />
        <link rel="canonical" href={pageUrl} />
      </Helmet>

      <main className="max-w-3xl mx-auto px-6 py-16 print:py-4">

        {/* Breadcrumb */}
        <nav className="text-xs uppercase tracking-[0.2em] text-[#8C7B6B] mb-8 print:hidden">
          <Link to="/checklist" className="hover:text-[#2E4036] transition-colors">All checklists</Link>
          <span className="mx-2">›</span>
          <span className="text-[#2E4036]">{checklist.title}</span>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.35em] text-[#CC5833]">Free resource</p>
          <h1 className="font-display text-4xl md:text-5xl mt-3 text-[#1c1b1a]">
            {checklist.icon} {checklist.title}
          </h1>
          <p className="text-[#6a6661] mt-4 max-w-2xl">{checklist.description}</p>
        </div>

        {/* Email gate */}
        {!hasAccess ? (
          <div className="border border-stone-200 bg-white/70 p-8">
            <NewsletterSignup
              title="Unlock this checklist free"
              subtitle="Enter your email to access the full interactive checklist instantly."
              benefits={[
                { title: 'Interactive', description: 'Tick items as you complete them.' },
                { title: 'Saves progress', description: 'Come back and your ticks are still there.' },
                { title: 'Print-ready', description: 'Print a clean version to keep handy.' },
              ]}
              successTitle="Access unlocked"
              successBody="Scroll down to start your checklist."
              ctaLabel="Unlock the checklist"
              source={`checklist-${slug}`}
              tag="checklist"
              onSuccess={grantAccess}
            />
            {isVerifying && <p className="text-[#6a6661] mt-3 text-sm">Verifying…</p>}
            <p className="text-xs uppercase tracking-[0.2em] text-[#8C7B6B] mt-4 text-center">
              We respect your privacy. Unsubscribe anytime.
            </p>
          </div>
        ) : (
          <>
            {/* Progress bar */}
            <div className="mb-8 print:hidden">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-[0.25em] text-[#8C7B6B]">
                  {completedItems} of {totalItems} complete
                </span>
                <div className="flex gap-3">
                  <button
                    onClick={handlePrint}
                    className="text-xs uppercase tracking-[0.25em] text-[#2E4036] hover:text-[#CC5833] transition-colors border border-[#D4CFC6] px-3 py-1.5"
                  >
                    Print
                  </button>
                  <button
                    onClick={() => {
                      setChecked({});
                      localStorage.removeItem(storageKey);
                    }}
                    className="text-xs uppercase tracking-[0.25em] text-[#8C7B6B] hover:text-[#2E4036] transition-colors"
                  >
                    Reset
                  </button>
                </div>
              </div>
              <div className="h-2 bg-[#E8E3D9] rounded-none">
                <div
                  className="h-2 bg-[#2E4036] transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              {progress === 100 && (
                <p className="text-sm text-[#2E4036] mt-2 font-medium">
                  ✓ All done — well done! Share this checklist with someone who needs it.
                </p>
              )}
            </div>

            {/* Checklist sections */}
            {checklist.sections.map((section, si) => (
              <div key={si} className="mb-10">
                <h2 className="font-display text-xl text-[#2E4036] border-b border-[#D4CFC6] pb-3 mb-5">
                  {section.title}
                </h2>
                <ul className="space-y-3">
                  {section.items.map(item => (
                    <li
                      key={item.id}
                      className={`flex gap-4 p-4 border transition-colors cursor-pointer ${
                        checked[item.id]
                          ? 'border-[#2E4036]/30 bg-[#2E4036]/5'
                          : 'border-stone-200 bg-white/70 hover:border-[#8C7B6B]'
                      }`}
                      onClick={() => toggleItem(item.id)}
                    >
                      {/* Checkbox */}
                      <div className={`flex-shrink-0 w-5 h-5 mt-0.5 border-2 flex items-center justify-center transition-colors ${
                        checked[item.id] ? 'bg-[#2E4036] border-[#2E4036]' : 'border-[#8C7B6B]'
                      }`}>
                        {checked[item.id] && (
                          <svg className="w-3 h-3 text-[#F2F0E9]" fill="none" viewBox="0 0 12 12">
                            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      {/* Content */}
                      <div className="flex-1">
                        <p className={`text-sm leading-snug ${checked[item.id] ? 'line-through text-[#8C7B6B]' : 'text-[#2d2b29]'}`}>
                          {item.text}
                        </p>
                        {item.tip && !checked[item.id] && (
                          <p className="text-xs text-[#8C7B6B] mt-1 leading-snug">
                            💡 {item.tip}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Bottom CTAs */}
            <div className="mt-10 border-l-4 border-[#CC5833] bg-[#F2F0E9] p-6 print:hidden">
              <p className="text-xs uppercase tracking-[0.3em] text-[#CC5833]">Need personal support?</p>
              <h3 className="font-display text-xl text-[#2E4036] mt-2">Book a session with Sista Kyerewaa</h3>
              <p className="text-sm text-[#5f5b56] mt-2">
                1-on-1 guidance for integration, health questions, or document support — tailored to your situation.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <Link
                  to="/book"
                  className="inline-flex items-center justify-center px-5 py-2.5 bg-[#2E4036] text-[#F2F0E9] text-xs uppercase tracking-[0.25em] hover:bg-[#1a2a20] transition-colors"
                >
                  Book a session
                </Link>
                <Link
                  to="/checklist"
                  className="inline-flex items-center justify-center px-5 py-2.5 border border-[#8C7B6B]/40 text-xs uppercase tracking-[0.25em] text-[#2E4036] hover:bg-[#e7e4df] transition-colors"
                >
                  View all checklists
                </Link>
              </div>
            </div>

            {/* Print-only footer */}
            <div className="hidden print:block mt-8 text-xs text-[#8C7B6B] border-t border-[#D4CFC6] pt-4">
              Downloaded from sistakyerewaa.com — free resources for African families in Germany.
            </div>
          </>
        )}
      </main>
    </>
  );
}
